import { clientStore } from 'ats/pages/clients/ClientStore';
import {
	deserializeColumnState,
	serializeColumnState,
	normalizeColumnStateForCompare
} from './columnState';
import {
	getCurrentUser,
	getCurrentUserId,
	resolveUserRefs,
	serializeUserRefs
} from './userRef';
import {
	normalizeQueryState,
	normalizeUiState,
	pickQueryForView,
	serializeOptionValues,
	captureSearchFilter,
	resolveSearchFilterForApply
} from './viewStateUtils';
import { syncFilterDrawerProps } from './syncFilterDrawerProps';

const EMPTY_QUERY = { sort: { column: 'createdAt', asc: false } };

function resolveIndustryOptions(values, options) {
	if (!values?.length) return null;
	return values
		.map(value =>
			options.find(
				option =>
					option.value === value ||
					option.value === Number(value) ||
					option.slug === value
			)
		)
		.filter(Boolean);
}

export function getClientsFactoryUiState() {
	return {
		searchFilter: null,
		industryFilter: null,
		sizeFilter: null,
		accountManagerFilter: null,
		primaryContactFilter: null,
		showOnlyWithOpenJobsFilter: false
	};
}

export function captureClientsListView(page) {
	const currentUserId = getCurrentUserId();

	return {
		ui: {
			searchFilter: captureSearchFilter(
				page.state.searchFilter,
				page.listStore.store.query
			),
			industryFilter: serializeOptionValues(page.state.industryFilter),
			sizeFilter: page.state.sizeFilter || null,
			accountManagerFilter: serializeUserRefs(
				page.state.accountManagerFilter,
				currentUserId
			),
			primaryContactFilter: page.state.primaryContactFilter || null,
			showOnlyWithOpenJobsFilter: !!page.state.showOnlyWithOpenJobsFilter
		},
		query: pickQueryForView(page.listStore.store.query),
		columnState: serializeColumnState(page.state.visibleColumns)
	};
}

export function getClientsFactorySnapshot() {
	return {
		ui: {
			searchFilter: null,
			industryFilter: null,
			sizeFilter: null,
			accountManagerFilter: null,
			primaryContactFilter: null,
			showOnlyWithOpenJobsFilter: false
		},
		query: {
			sort: { column: 'createdAt', asc: false }
		},
		columnState: serializeColumnState(clientStore.defaultTableColumnsOptions)
	};
}

function buildUiStateFromSaved(ui) {
	const currentUser = getCurrentUser();

	return {
		searchFilter: ui.searchFilter || null,
		industryFilter: resolveIndustryOptions(
			ui.industryFilter,
			clientStore.industryOptions
		),
		sizeFilter: ui.sizeFilter || null,
		accountManagerFilter: resolveUserRefs(ui.accountManagerFilter, currentUser),
		primaryContactFilter: ui.primaryContactFilter || null,
		showOnlyWithOpenJobsFilter: !!ui.showOnlyWithOpenJobsFilter
	};
}

export function applyClientsListView(
	page,
	snapshot,
	{ isFactoryDefault = false } = {}
) {
	const ui = buildUiStateFromSaved(snapshot.ui || {});
	ui.searchFilter = resolveSearchFilterForApply(
		ui.searchFilter,
		snapshot.query
	);
	const visibleColumns = isFactoryDefault
		? clientStore.defaultTableColumnsOptions
		: deserializeColumnState(
				snapshot.columnState,
				clientStore.tableColumnsOptions,
				clientStore.defaultTableColumnsOptions
			);

	page.listStore.store.resetQuery();
	page.listStore.store.setQuery(
		'sort',
		snapshot.query?.sort || { column: 'createdAt', asc: false }
	);

	page.listStore.store.setQuery('text', ui.searchFilter || null);

	const query = snapshot.query || {};
	if (query.industry) page.listStore.store.setQuery('industry', query.industry);
	if (query.size) page.listStore.store.setQuery('size', query.size);
	if (query.accountManager?.length) {
		page.listStore.store.setQuery('accountManager', query.accountManager);
	} else if (ui.accountManagerFilter?.length) {
		page.listStore.store.setQuery(
			'accountManager',
			ui.accountManagerFilter.map(user => user.id)
		);
	}
	if (query.primaryContact)
		page.listStore.store.setQuery('primaryContact', query.primaryContact);
	if (query.showOnlyWithOpenJobs != null) {
		page.listStore.store.setQuery(
			'showOnlyWithOpenJobs',
			query.showOnlyWithOpenJobs
		);
	}

	return new Promise(resolve => {
		page.setState({ ...ui, visibleColumns }, () => {
			syncFilterDrawerProps(page);
			page.handlePaginationChange(1);
			resolve();
		});
	});
}

export function applyClientsFactoryDefaults(page) {
	return applyClientsListView(page, getClientsFactorySnapshot(), {
		isFactoryDefault: true
	});
}

export function resolveClientsViewSnapshot(view) {
	if (view.systemKey === 'default') {
		return getClientsFactorySnapshot();
	}
	return {
		ui: view.filterState?.ui || {},
		query: view.filterState?.query || EMPTY_QUERY,
		columnState: view.columnState
	};
}

export function normalizeClientsSnapshot(snapshot) {
	return {
		ui: normalizeUiState(snapshot.ui),
		query: normalizeQueryState(snapshot.query, EMPTY_QUERY.sort),
		columnState: normalizeColumnStateForCompare(
			snapshot.columnState,
			clientStore.defaultTableColumnsOptions
		)
	};
}

export function serializeClientsViewForSave(snapshot) {
	return {
		filterState: {
			ui: snapshot.ui,
			query: snapshot.query
		},
		columnState: snapshot.columnState
	};
}
